'use client';

import {useState, useMemo} from 'react';
import {useLocale} from 'next-intl';

/**
 * Espace Vidéos (médiathèque UTSS).
 * - Galerie filtrable par catégorie
 * - Lecteur modal (embed YouTube via `youtube_id`)
 * - Les entrées sans `youtube_id` s'affichent en « Bientôt disponible »
 *   (l'équipe UTSS renseigne l'identifiant YouTube de chaque vidéo).
 */

type Categorie = 'presentation' | 'terrain' | 'temoignages' | 'campagnes' | 'evenements';

interface Video {
  titre_fr: string;
  titre_ar: string;
  titre_en: string;
  categorie: Categorie;
  youtube_id: string; // ex: "dQw4w9WgXcQ" — vide = à venir
  duree?: string;
}

const VIDEOS: Video[] = [
  {titre_fr: "Présentation de l'UTSS", titre_ar: 'تقديم الاتحاد', titre_en: 'About UTSS', categorie: 'presentation', youtube_id: '', duree: ''},
  {titre_fr: 'Nos programmes sociaux', titre_ar: 'برامجنا الاجتماعية', titre_en: 'Our social programs', categorie: 'presentation', youtube_id: '', duree: ''},
  {titre_fr: 'Reportage : aide aux familles démunies', titre_ar: 'روبورتاج: مساعدة العائلات المعوزة', titre_en: 'Report: aid to needy families', categorie: 'terrain', youtube_id: '', duree: ''},
  {titre_fr: 'Jardins de solidarité — sur le terrain', titre_ar: 'رياض التضامن — ميدانيا', titre_en: 'Solidarity gardens in the field', categorie: 'terrain', youtube_id: '', duree: ''},
  {titre_fr: "Témoignage d'un bénéficiaire", titre_ar: 'شهادة مستفيد', titre_en: 'A beneficiary testimony', categorie: 'temoignages', youtube_id: '', duree: ''},
  {titre_fr: 'Microcrédit : une réussite', titre_ar: 'قرض صغير: قصة نجاح', titre_en: 'Microcredit: a success story', categorie: 'temoignages', youtube_id: '', duree: ''},
  {titre_fr: 'Campagne vague de froid', titre_ar: 'حملة موجة البرد', titre_en: 'Cold wave campaign', categorie: 'campagnes', youtube_id: '', duree: ''},
  {titre_fr: 'Campagne rentrée scolaire', titre_ar: 'حملة العودة المدرسية', titre_en: 'Back-to-school campaign', categorie: 'campagnes', youtube_id: '', duree: ''},
  {titre_fr: 'Journée portes ouvertes Dar Ourabi', titre_ar: 'يوم أبواب مفتوحة دار العرابي', titre_en: 'Dar Ourabi open day', categorie: 'evenements', youtube_id: '', duree: ''},
];

const LABELS: Record<string, {
  home: string; crumb: string; title: string; intro: string;
  all: string; soon: string; cats: Record<Categorie, string>;
}> = {
  fr: {
    home: 'Accueil', crumb: 'Vidéos', title: 'Espace Vidéos',
    intro: "Découvrez l'UTSS en images : présentations, reportages de terrain, témoignages de bénéficiaires et campagnes de solidarité.",
    all: 'Toutes', soon: 'Bientôt disponible',
    cats: {presentation: 'Présentation', terrain: 'Sur le terrain', temoignages: 'Témoignages', campagnes: 'Campagnes', evenements: 'Événements'},
  },
  ar: {
    home: 'الرئيسية', crumb: 'فيديوهات', title: 'فضاء الفيديوهات',
    intro: 'اكتشف الاتحاد التونسي للتضامن الاجتماعي بالصورة: تقديمات، روبورتاجات ميدانية، شهادات المستفيدين وحملات التضامن.',
    all: 'الكل', soon: 'قريبا',
    cats: {presentation: 'تقديم', terrain: 'ميدانيا', temoignages: 'شهادات', campagnes: 'حملات', evenements: 'فعاليات'},
  },
  en: {
    home: 'Home', crumb: 'Videos', title: 'Video Space',
    intro: 'Discover UTSS in pictures: presentations, field reports, beneficiary testimonials and solidarity campaigns.',
    all: 'All', soon: 'Coming soon',
    cats: {presentation: 'About', terrain: 'In the field', temoignages: 'Testimonials', campagnes: 'Campaigns', evenements: 'Events'},
  },
};

const CAT_ORDER: Categorie[] = ['presentation', 'terrain', 'temoignages', 'campagnes', 'evenements'];

export default function VideosPage() {
  const locale = useLocale();
  const l = LABELS[locale] || LABELS.fr;
  const [filter, setFilter] = useState<Categorie | 'all'>('all');
  const [playing, setPlaying] = useState<Video | null>(null);

  const titreOf = (v: Video) => (locale === 'ar' ? v.titre_ar : locale === 'en' ? v.titre_en : v.titre_fr);

  const filtered = useMemo(
    () => (filter === 'all' ? VIDEOS : VIDEOS.filter(v => v.categorie === filter)),
    [filter]
  );

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <nav className="text-sm text-muted mb-6">
        <a href={`/${locale}`} className="hover:text-primary">{l.home}</a>
        <span className="mx-2">/</span>
        <span className="text-foreground">{l.crumb}</span>
      </nav>

      <h1 className="text-3xl font-bold text-primary mb-2">{l.title}</h1>
      <p className="text-muted mb-6 max-w-3xl">{l.intro}</p>

      {/* Filtres catégories */}
      <div className="flex flex-wrap gap-2 mb-8">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors cursor-pointer ${
            filter === 'all' ? 'bg-primary text-white' : 'bg-surface text-foreground/70 hover:bg-primary/10'
          }`}
        >
          {l.all}
        </button>
        {CAT_ORDER.map(c => (
          <button
            key={c}
            onClick={() => setFilter(c)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors cursor-pointer ${
              filter === c ? 'bg-primary text-white' : 'bg-surface text-foreground/70 hover:bg-primary/10'
            }`}
          >
            {l.cats[c]}
          </button>
        ))}
      </div>

      {/* Grille vidéos */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {filtered.map((v, i) => {
          const playable = !!v.youtube_id;
          return (
            <button
              key={i}
              onClick={() => playable && setPlaying(v)}
              disabled={!playable}
              className={`text-start rounded-xl border border-border overflow-hidden bg-white transition-shadow ${
                playable ? 'hover:shadow-lg cursor-pointer' : 'opacity-80 cursor-default'
              }`}
            >
              <div className="aspect-video bg-surface relative flex items-center justify-center overflow-hidden">
                {playable ? (
                  <img
                    src={`https://img.youtube.com/vi/${v.youtube_id}/hqdefault.jpg`}
                    alt={titreOf(v)}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-xs text-muted">{l.soon}</span>
                )}
                {playable && (
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="w-14 h-14 rounded-full bg-black/55 flex items-center justify-center">
                      <svg className="w-7 h-7 text-white ms-0.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                    </span>
                  </span>
                )}
              </div>
              <div className="p-4">
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                  {l.cats[v.categorie]}
                </span>
                <h3 className="font-semibold text-foreground mt-2 leading-snug" dir="auto">{titreOf(v)}</h3>
                {v.duree && <p className="text-xs text-muted mt-1">{v.duree}</p>}
              </div>
            </button>
          );
        })}
      </div>

      {/* Lecteur modal */}
      {playing && (
        <div
          className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4"
          onClick={() => setPlaying(null)}
        >
          <div className="w-full max-w-4xl" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-white font-semibold" dir="auto">{titreOf(playing)}</h2>
              <button
                onClick={() => setPlaying(null)}
                className="text-white/80 hover:text-white p-2 cursor-pointer"
                aria-label="Fermer"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="aspect-video bg-black rounded-lg overflow-hidden">
              <iframe
                src={`https://www.youtube.com/embed/${playing.youtube_id}?autoplay=1`}
                title={titreOf(playing)}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
